import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card'
import { HealthCardReader } from '@/components/HealthCardReader'
import { ExaminationRoom as BaseExaminationRoom } from '@/pages/clinic/examinationRooms/columns'
import { queueMachineCallerService } from '@/services/queue_machine_caller_service'
import { appointmentService } from '@/services/appointment_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import {
  ChevronUp,
  ChevronDown,
  AlertCircle,
  RefreshCw,
  Clock,
  User,
  Hash,
  Check,
  Info,
  RotateCcw,
  CheckCircle2,
  Bookmark,
  BookmarkCheck,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useQueueMachineCallerCurrentState } from '@/hooks/useQueueMachineCallerCurrentState'
import { useQueueMachine } from '@/hooks/useQueueMachine'
import { Badge } from '@/components/ui/badge'
import { useBookmarks } from '@/stores/useBookmarks'

// ChannelFunction
enum ChannelFunction {
  DEFAULT = 'DEFAULT',
  INTERNAL = 'INTERNAL',
  THIRD_PARTY = 'THIRD_PARTY',
}

// 擴展基本的診間類型，加入叫號機相關屬性
type ExtendedExaminationRoom = BaseExaminationRoom & {
  queue_machine_id?: number
  queue_machine?: {
    channel: string
    show_patient_name: boolean
  }
}

type QueueMachineCallerProps = {
  examinationRoom?: ExtendedExaminationRoom
  isLoading?: boolean
  onSuccess?: () => void
}

export function QueueMachineCaller({ examinationRoom, isLoading }: QueueMachineCallerProps) {
  const { token } = useAuth()
  const { toggleBookmark, isBookmarked } = useBookmarks()

  // 使用 hook 獲取叫號機當前狀態
  const {
    currentState,
    isLoading: isLoadingState,
    isError: isErrorState,
    mutate: refreshCurrentState,
  } = useQueueMachineCallerCurrentState({
    queueMachineId: examinationRoom?.queue_machine_id || null,
  })

  // 使用 useQueueMachine hook 獲取叫號機詳細資訊
  const { queueMachine, isLoading: isLoadingQueueMachine } = useQueueMachine(
    examinationRoom?.queue_machine_id,
  )

  const [customNumber, setCustomNumber] = useState<string>('')
  const [patientName, setPatientName] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  // 檢查是否有叫號機
  const hasQueueMachine = examinationRoom && examinationRoom.queue_machine_id

  // 書籤相關邏輯
  const bookmarkKey = `examination_room_queue_machine_${examinationRoom?.examination_room_id}`
  const bookmarkUrl = `clinic/rooms?e=${examinationRoom?.examination_room_id}&p=queue_machine_caller`
  const isCurrentBookmarked = isBookmarked(bookmarkKey, bookmarkUrl)

  // 鍵盤事件處理函數
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // 只有在沒有處理中且有叫號機時才處理鍵盤事件
      if (!isProcessing && !isLoadingState && hasQueueMachine) {
        if (event.key === 'ArrowDown') {
          // 上箭頭鍵 - 下一位
          handleIncreaseNumber()
        } else if (event.key === 'ArrowUp') {
          // 下箭頭鍵 - 上一位
          handleDiscountNumber()
        }
      }
    },
    // handleIncreaseNumber / handleDiscountNumber 宣告在此 callback 之後（加入相依會造成 TDZ），
    // 且每次 render 都會重建，加入後會讓鍵盤監聽器每次 render 反覆移除/註冊，故刻意省略以維持既有行為
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isProcessing, isLoadingState, hasQueueMachine],
  )

  // 顯示錯誤訊息的幫助函數
  const showErrorToast = (error: unknown, defaultMessage = '發生未知錯誤') => {
    let description = defaultMessage

    // 如果是 API 回應的錯誤
    const apiError =
      typeof error === 'object' && error !== null && 'error' in error
        ? (error as { error?: { message?: string; details?: string } }).error
        : undefined

    if (apiError) {
      description = apiError.message || defaultMessage
      // 如果有詳細錯誤信息，顯示在描述中
      if (apiError.details && apiError.details !== apiError.message) {
        description = `${description} (${apiError.details})`
      }
    } else if (error instanceof Error) {
      description = error.message
    }

    toast.error(description)
  }

  const handleIncreaseNumber = async () => {
    if (!hasQueueMachine || !token) return

    setIsProcessing(true)
    try {
      const response = await queueMachineCallerService.increaseNumber(
        examinationRoom.queue_machine_id,
        1,
        token,
        isDefaultChannel() && queueMachine?.show_patient_name ? patientName : undefined,
      )

      if (response.success && response.data) {
        // 更新叫號機狀態
        refreshCurrentState()

        // 顯示成功訊息，包含病患名稱（如果有）
        const patientInfo = response.data.patientName
          ? `${response.data.currentName} (${response.data.patientName})`
          : response.data.currentName

        toast.success(`目前叫號: ${patientInfo}`)
      } else {
        showErrorToast(response, '增加號碼時發生錯誤')
      }
    } catch (error) {
      showErrorToast(error, '增加號碼時發生未知錯誤')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDiscountNumber = async () => {
    if (!hasQueueMachine || !token) return

    setIsProcessing(true)
    try {
      const response = await queueMachineCallerService.discountNumber(
        examinationRoom.queue_machine_id,
        1,
        token,
        isDefaultChannel() && queueMachine?.show_patient_name ? patientName : undefined,
      )

      if (response.success && response.data) {
        // 更新叫號機狀態
        refreshCurrentState()

        // 顯示成功訊息，包含病患名稱（如果有）
        const patientInfo = response.data.patientName
          ? `${response.data.currentName} (${response.data.patientName})`
          : response.data.currentName

        toast.success(`目前叫號: ${patientInfo}`)
      } else {
        showErrorToast(response, '減少號碼時發生錯誤')
      }
    } catch (error) {
      showErrorToast(error, '減少號碼時發生未知錯誤')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleSetNumber = async () => {
    if (!hasQueueMachine || !token || !customNumber) return

    // 驗證數字，如果是負數則設為0
    const numberValue = parseInt(customNumber, 10)
    if (isNaN(numberValue) || numberValue < 0) {
      setCustomNumber('0')
      return
    }

    setIsProcessing(true)
    try {
      const response = await queueMachineCallerService.setNumber(
        examinationRoom.queue_machine_id,
        numberValue,
        token,
        isDefaultChannel() && queueMachine?.show_patient_name ? patientName : undefined,
      )

      if (response.success && response.data) {
        // 更新叫號機狀態
        refreshCurrentState()

        // 顯示成功訊息，包含病患名稱（如果有）
        const patientInfo = response.data.patientName
          ? `${response.data.currentName} (${response.data.patientName})`
          : response.data.currentName

        toast.success(`目前叫號: ${patientInfo}`)
        // 清空自定義號碼輸入
        setCustomNumber('')
      } else {
        showErrorToast(response, '設定號碼時發生錯誤')
      }
    } catch (error) {
      showErrorToast(error, '設定號碼時發生未知錯誤')
    } finally {
      setIsProcessing(false)
    }
  }

  // 標記當前預約為完成並呼叫下一位
  const handleCompleteAndNext = async () => {
    if (!hasQueueMachine || !token || !currentState?.appointmentId) return

    setIsProcessing(true)
    try {
      // 1. 先將當前預約標記為完成

      const completeResponse = await appointmentService.updateAppointmentToCompleted(
        currentState.appointmentId,
        token,
      )

      if (!completeResponse.success) {
        showErrorToast(completeResponse, '無法將預約標記為完成')
        return
      }

      // // 2. 呼叫下一位
      // const nextResponse = await queueMachineCallerService.increaseNumber(
      //   examinationRoom.queue_machine_id,
      //   1,
      //   token,
      //   isDefaultChannel() && queueMachine?.show_patient_name ? patientName : undefined,
      // )

      refreshCurrentState()
    } catch (error) {
      showErrorToast(error, '標記完成並呼叫下一位時發生錯誤')
    } finally {
      setIsProcessing(false)
    }
  }

  // 檢查是否為預設頻道，使用 useQueueMachine hook 獲取的資料
  const isDefaultChannel = () => {
    return queueMachine?.channel === ChannelFunction.DEFAULT
  }

  // 書籤切換處理函數
  const handleBookmarkToggle = () => {
    if (!examinationRoom) return

    const bookmark = {
      key: bookmarkKey,
      url: bookmarkUrl,
      name: `叫號機 - 診間${examinationRoom.name}`,
    }

    toggleBookmark(bookmark)

    toast.success(isCurrentBookmarked ? '已移除書籤' : '已加入書籤')
  }

  // 添加和移除鍵盤事件監聽器
  useEffect(() => {
    // 添加鍵盤事件監聽器
    window.addEventListener('keydown', handleKeyDown)

    // 清理函數 - 移除鍵盤事件監聽器
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  // 如果診間資料或叫號機資料正在載入中，顯示載入中狀態
  if (isLoading || isLoadingQueueMachine) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">載入中...</p>
        </div>
      </div>
    )
  }

  // 如果沒有叫號機，顯示提示訊息
  if (!hasQueueMachine) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <h3 className="text-lg font-medium">此診間尚未設定叫號機</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          請先在診間基本資料中設定叫號機，才能使用叫號功能。
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-2xl font-bold tracking-tight">叫號機控制</h2>
        <div className="flex items-center gap-3">
          <div>
            {hasQueueMachine ? (
              <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 bg-primary/5">
                <Hash className="h-3.5 w-3.5" />
                <span>診間叫號機: {examinationRoom?.name || ''}</span>
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="flex items-center gap-1 px-3 py-1 bg-destructive/10 text-destructive"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                <span>此診間未設定叫號機</span>
              </Badge>
            )}
          </div>

          {/* 書籤按鈕 */}
          {hasQueueMachine && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBookmarkToggle}
              className={`p-2 ${isCurrentBookmarked ? 'text-yellow-500 hover:text-yellow-600' : 'text-muted-foreground hover:text-foreground'}`}
              title={isCurrentBookmarked ? '移除書籤' : '加入書籤'}
            >
              {isCurrentBookmarked ? (
                <BookmarkCheck className="h-5 w-5" />
              ) : (
                <Bookmark className="h-5 w-5" />
              )}
            </Button>
          )}
        </div>
      </div>

      {!hasQueueMachine ? (
        <Card className="shadow-md border-destructive/20">
          <CardHeader className="bg-destructive/5">
            <CardTitle className="text-xl text-destructive">無法使用叫號機</CardTitle>
            <CardDescription>此診間目前未設定叫號機，請先在診間設定中新增叫號機</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex items-center p-4 bg-destructive/5 rounded-md">
              <Info className="h-5 w-5 mr-2 text-destructive" />
              <p className="text-sm">需要先在診間設定中新增叫號機，才能使用叫號功能</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 當前狀態卡片 */}
          <Card className="shadow-md overflow-hidden border-primary/20">
            <CardHeader className="bg-primary/5 pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-xl">目前叫號狀態</CardTitle>
                {currentState && !isLoadingState && !isErrorState && (
                  <Badge variant="outline" className="bg-primary/10">
                    已連線
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {isLoadingState ? (
                <div className="flex flex-col items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-sm text-muted-foreground">載入中...</p>
                </div>
              ) : isErrorState ? (
                <div className="flex flex-col items-center py-8 text-destructive">
                  <AlertCircle className="h-12 w-12 mb-2" />
                  <p className="text-lg font-medium">載入失敗</p>
                  <p className="mt-1 text-sm">無法取得叫號機狀態</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => refreshCurrentState()}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" /> 重試
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="relative">
                    <div className="text-6xl font-bold text-primary mb-3">
                      {currentState?.currentName || '000'}
                    </div>
                    <Badge variant="outline" className="absolute -top-2 -right-12 bg-primary/10">
                      {currentState?.currentNumber || 0}
                    </Badge>
                  </div>

                  {currentState?.patientName && (
                    <div className="flex items-center mt-1 text-sm text-muted-foreground">
                      <User className="h-3.5 w-3.5 mr-1" />
                      <span>{currentState.patientName}</span>
                    </div>
                  )}

                  {/* 標記完成並下一位按鈕 */}
                  {!!currentState.currentNumber &&
                    currentState.appointment?.appointment_status === 'IN_PROGRESS' && (
                      <div className="flex justify-center mt-4">
                        <Button
                          size="lg"
                          className="h-16 w-full text-lg bg-green-600 hover:bg-green-700"
                          onClick={handleCompleteAndNext}
                          disabled={isProcessing || isLoadingState}
                          title="標記本病患看診完成"
                        >
                          <CheckCircle2 className="h-6 w-6 mr-1.5" /> 標記本病患看診完成
                        </Button>
                      </div>
                    )}

                  {/* 完成看診的顯示 */}
                  {currentState?.appointment?.appointment_status === 'COMPLETED' && (
                    <div className="flex justify-center mt-4">
                      <Badge variant="outline" className="bg-green-600/10 text-green-600">
                        <CheckCircle2 className="h-4 w-4 mr-1" /> 已完成
                      </Badge>
                    </div>
                  )}

                  <div className="flex items-center mt-4 space-x-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refreshCurrentState()}
                      disabled={isProcessing}
                      className="flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                      <span>刷新</span>
                    </Button>

                    {currentState?.lastUpdated && (
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>
                          更新於 {new Date(currentState.lastUpdated).toLocaleTimeString('zh-TW')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
            {currentState && !isLoadingState && !isErrorState && (
              <CardFooter className="bg-muted/30 py-2 px-6 flex justify-center text-xs text-muted-foreground">
                <Clock className="h-3 w-3 mr-1" />
                <span>每 30 秒自動刷新</span>
              </CardFooter>
            )}
          </Card>
          {queueMachine.channel !== ChannelFunction.THIRD_PARTY && (
            <Card className="shadow-md overflow-hidden">
              <CardHeader className="bg-primary/5 pb-2">
                <CardTitle className="text-xl">叫號控制</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {/* 增減號碼按鈕 */}
                  <div className="flex justify-center space-x-4">
                    <Button
                      size="lg"
                      className="h-16 w-28 text-lg"
                      onClick={handleDiscountNumber}
                      disabled={
                        isProcessing ||
                        isLoadingState ||
                        (currentState?.hasPrevious != null && !currentState?.hasPrevious)
                      }
                      variant="outline"
                      title="返回上一位病患"
                    >
                      <ChevronDown className="h-6 w-6 mr-1.5" /> 上一位
                    </Button>
                    <Button
                      size="lg"
                      className="h-16 w-28 text-lg"
                      onClick={handleIncreaseNumber}
                      disabled={
                        isProcessing ||
                        isLoadingState ||
                        (currentState?.hasNext != null && !currentState?.hasNext)
                      }
                      title="呼叫下一位病患"
                    >
                      <ChevronUp className="h-6 w-6 mr-1.5" /> 下一位
                    </Button>
                  </div>

                  {/* 設定特定號碼 */}
                  <div className="pt-4 border-t">
                    <div className="flex flex-col space-y-2">
                      <label className="text-sm font-medium">設定特定號碼</label>
                      <div className="flex items-end space-x-2">
                        <div className="flex-1">
                          <Input
                            type="number"
                            placeholder="輸入號碼"
                            value={customNumber}
                            onChange={(e) => setCustomNumber(e.target.value)}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value, 10)
                              if (isNaN(value) || value < 0) {
                                setCustomNumber('0')
                              }
                            }}
                            className="text-lg py-6"
                            disabled={isProcessing || isLoadingState}
                            min="0"
                          />
                        </div>
                        <Button
                          className="h-12 px-6"
                          onClick={handleSetNumber}
                          disabled={isProcessing || isLoadingState || !customNumber}
                          title="設定指定的叫號號碼"
                        >
                          <Check className="h-4 w-4 mr-2" /> 設定
                        </Button>
                      </div>
                      <div className="flex justify-end mt-2">
                        <Button
                          variant="outline"
                          className="h-10"
                          onClick={() => {
                            if (hasQueueMachine && token) {
                              setIsProcessing(true)
                              queueMachineCallerService
                                .setNumber(examinationRoom.queue_machine_id, 0, token)
                                .then((response) => {
                                  if (response.success) {
                                    refreshCurrentState()
                                    toast.success('重置成功')
                                  } else {
                                    showErrorToast(response, '重置叫號機時發生錯誤')
                                  }
                                })
                                .catch((error) => {
                                  showErrorToast(error, '重置叫號機時發生未知錯誤')
                                })
                                .finally(() => {
                                  setIsProcessing(false)
                                })
                            }
                          }}
                          disabled={isProcessing || isLoadingState}
                          title="重置叫號機號碼為0"
                        >
                          <RotateCcw className="h-4 w-4 mr-2" /> 重置
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        輸入預約的流水號來設定目前叫號狀態
                      </p>
                    </div>
                  </div>

                  {/* 病患姓名輸入欄位 - 僅在預設頻道且需顯示病患姓名時顯示 */}
                  {isDefaultChannel() && queueMachine?.show_patient_name && (
                    <div className="pt-4 border-t">
                      <div className="flex flex-col space-y-2">
                        <div className="flex justify-between items-center">
                          <label className="text-sm font-medium">病患姓名</label>
                          <HealthCardReader
                            onSuccess={(data) => {
                              // 填入健保卡資料到病患姓名
                              setPatientName(data.full_name)
                              toast.success('讀取成功')
                            }}
                            onFailed={() => {
                              toast.error('讀取失敗')
                            }}
                            className="mb-2"
                          />
                        </div>
                        <div className="flex items-end space-x-2">
                          <div className="flex-1">
                            <Input
                              type="text"
                              placeholder="輸入病患姓名"
                              value={patientName}
                              onChange={(e) => setPatientName(e.target.value)}
                              className="text-lg py-6"
                              disabled={isProcessing || isLoadingState}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          輸入病患姓名，將顯示在叫號畫面上
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
